import React from 'react';
import { Twitter, Github, Linkedin } from 'lucide-react';
import { ThemeMode } from '../types';
import { useI18n } from '../i18n/I18nContext';
import { SmartLink } from './SmartLink';

interface FooterProps {
  theme: ThemeMode;
  onSelectCategoryById?: (id: string) => void;
  onNavigate?: (nav: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ 
  theme, 
  onSelectCategoryById,
  onNavigate 
}) => {
  const isDark = theme === 'dark';
  const { t, getLangUrl } = useI18n();

  const getNavHref = (nav: string): string => {
    switch (nav) {
      case 'all-tools': return getLangUrl('/tools');
      case 'categories': return getLangUrl('/categories');
      case 'about': return getLangUrl('/about');
      case 'blog': return getLangUrl('/blog');
      case 'contact': return getLangUrl('/contact');
      case 'privacy-policy': return getLangUrl('/privacy-policy');
      case 'terms-of-service': return getLangUrl('/terms-of-service');
      default: return getLangUrl(`/${nav}`);
    }
  };

  const getCategoryHref = (id: string): string => {
    return getLangUrl(`/${id}`);
  };

  return (
    <footer className={`border-t theme-transition ${
      isDark ? 'bg-[#06080e] border-slate-800/80 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'
    }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-6 sm:pt-12 pb-6 sm:pb-8">
        
        {/* Top 4 Columns Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 mb-6 sm:mb-12">
          
          {/* Column 1: Brand & Socials */}
          <div>
            <div className="flex items-center gap-2 sm:gap-3 select-none mb-2 sm:mb-4">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="footerLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#8B5CF6" />
                    <stop offset="100%" stopColor="#7C3AED" />
                  </linearGradient>
                </defs>
                <path d="M 172.5 110 H 290 C 367.32 110 430 172.68 430 250 C 430 327.32 367.32 390 290 390 H 172.5 C 151.24 390 134 372.76 134 351.5 C 134 330.24 151.24 313 172.5 313 H 290 C 324.79 313 353 284.79 353 250 C 353 215.21 324.79 187 290 187 H 172.5 C 151.24 187 134 169.76 134 148.5 C 134 127.24 151.24 110 172.5 110 Z" fill="url(#footerLogoGrad)" />
                <rect x="200" y="210" width="46" height="80" rx="23" fill="url(#footerLogoGrad)" />
              </svg>
              <div className="flex items-center tracking-tight text-base sm:text-xl font-black">
                <span className={isDark ? 'text-white' : 'text-slate-900'}>DUALIS</span>
                <span className="ml-1.5 sm:ml-2 text-purple-600 font-black">TOOLS</span>
              </div>
            </div>

            <p className={`text-[11px] sm:text-sm leading-tight sm:leading-relaxed mb-3 sm:mb-5 ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}>
              {t('footerDescription', 'Dualis Tools offers over 450+ free, instant, privacy-first web utilities designed for ultimate productivity.')}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-2 sm:gap-2.5">
              <a 
                href="https://x.com/DualisTools" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className={`p-2 sm:p-2.5 rounded-full border transition-colors ${
                  isDark 
                    ? 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700' 
                    : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 shadow-sm'
                }`}
              >
                <Twitter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </a>
              <a 
                href="https://github.com/dualis-tools" 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className={`p-2 sm:p-2.5 rounded-full border transition-colors ${
                  isDark 
                    ? 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700' 
                    : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 shadow-sm'
                }`}
              >
                <Github className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </a>
              <a 
                href="#linkedin" 
                onClick={(e) => e.preventDefault()}
                aria-label="LinkedIn"
                className={`p-2 sm:p-2.5 rounded-full border transition-colors ${
                  isDark 
                    ? 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700' 
                    : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 shadow-sm'
                }`}
              >
                <Linkedin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className={`text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 sm:mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {t('quickLinks', 'Quick Links')}
            </h3>
            <ul className="space-y-1.5 sm:space-y-2.5 text-[11px] sm:text-sm">
              {[
                { name: t('navAllTools', 'All Tools'), nav: 'all-tools' },
                { name: t('navCategories', 'Categories'), nav: 'categories' },
                { name: t('navAbout', 'About'), nav: 'about' },
                { name: t('navBlog', 'Blog'), nav: 'blog' },
                { name: t('navContact', 'Contact'), nav: 'contact' },
              ].map((item) => (
                <li key={item.nav}>
                  <SmartLink 
                    href={getNavHref(item.nav)}
                    onClick={() => onNavigate && onNavigate(item.nav)}
                    className={`transition-colors text-left block ${
                      isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {item.name}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Popular Categories */}
          <div>
            <h3 className={`text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 sm:mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {t('toolCategories', 'Tool Categories')}
            </h3>
            <ul className="space-y-1.5 sm:space-y-2.5 text-[11px] sm:text-sm">
              {[
                { name: t('hubPdf', 'PDF Tools'), id: 'pdf-tools' },
                { name: t('hubImage', 'Image Tools'), id: 'image-tools' },
                { name: t('hubFinance', 'Finance Tools'), id: 'finance-tools' },
                { name: t('hubDev', 'Developer Tools'), id: 'developer-tools' },
                { name: t('hubCalc', 'Calculators'), id: 'calculators' },
                { name: t('hubData', 'Data Tools'), id: 'data-tools' },
                { name: t('hubUnit', 'Unit Converters'), id: 'unit-conversion' },
                { name: t('hubTimeDate', 'Time & Date'), id: 'time-date-tools' },
              ].map((cat) => (
                <li key={cat.id}>
                  <SmartLink 
                    href={getCategoryHref(cat.id)}
                    onClick={() => onSelectCategoryById && onSelectCategoryById(cat.id)}
                    className={`transition-colors text-left block ${
                      isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {cat.name}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: About */}
          <div>
            <h3 className={`text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 sm:mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {t('navAbout', 'About')}
            </h3>
            <p className={`text-[11px] sm:text-sm leading-tight sm:leading-relaxed mb-2.5 sm:mb-4 ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}>
              {t('footerAboutSub', 'Dualis Tools is a free online platform providing 450+ tools to make your daily tasks easier.')}
            </p>
            <SmartLink 
              href={getNavHref('about')}
              onClick={() => onNavigate && onNavigate('about')}
              className={`inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border text-[11px] sm:text-xs font-semibold transition-colors ${
                isDark 
                  ? 'border-purple-500/40 text-purple-300 hover:bg-purple-950/40 hover:border-purple-400' 
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400'
              }`}
            >
              {t('learnMore', 'Learn More')}
            </SmartLink>
          </div>

        </div>

        {/* Bottom Bar Divider & Legal */}
        <div className={`pt-4 sm:pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-4 text-[11px] sm:text-xs ${
          isDark ? 'border-slate-800/80 text-slate-500' : 'border-slate-200 text-slate-500'
        }`}>
          <div>
            © 2026 Dualis Tools. {t('allRightsReserved', 'All rights reserved.')}
          </div>
          <div className="flex items-center space-x-4 sm:space-x-6">
            <SmartLink 
              href={getNavHref('privacy-policy')}
              onClick={() => onNavigate && onNavigate('privacy-policy')}
              className="hover:underline"
            >
              {t('legalPrivacy', 'Privacy Policy')}
            </SmartLink>
            <SmartLink 
              href={getNavHref('terms-of-service')}
              onClick={() => onNavigate && onNavigate('terms-of-service')}
              className="hover:underline"
            >
              {t('legalTerms', 'Terms of Service')}
            </SmartLink>
          </div>
        </div>

      </div>
    </footer>
  );
};
